import { useMemo, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useCountry } from '@/lib/country'
import { parseAddressList } from '@/lib/emailHtml'
import { useAsync } from '@/lib/useAsync'
import { Dialog } from '@/components/ui/dialog'
import { EmailEditor } from '@/components/ui/emaileditor'
import { Field, Input } from '@/components/ui/form'
import { PageHeader } from '@/components/ui/page'
import { Badge, Busy, Button, Card, CardHeader, EmptyState, ErrorState } from '@/components/ui/primitives'
import { Table, Td, Th } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type { EmailPlaceholder, EmailTemplate } from '@/types/api'

/**
 * The wording a country's quotations go out with.
 *
 * <p>
 * Country-scoped like the letterhead and the terms document, because everything a customer
 * receives is: a Manila customer is written to in different words, from a different entity,
 * with a different office copied in.
 * </p>
 *
 * One template per country carries Default, and that is the one the Send dialog opens with.
 * The rest are there for the sends that are not the first one - a follow-up, a renewal.
 */
export function EmailTemplates() {
  const { can } = useAuth()
  const { current } = useCountry()
  const toast = useToast()

  const [editing, setEditing] = useState<EmailTemplate | 'new' | undefined>()
  const [busy, setBusy] = useState(false)

  const countryId = current?.countryId

  const templates = useAsync<EmailTemplate[]>(
    (signal) =>
      countryId
        ? api.get<EmailTemplate[]>(`/countries/${countryId}/email-templates`, undefined, signal)
        : Promise.resolve([]),
    [countryId],
  )

  // Served rather than listed here, so the tokens the menu offers and the tokens the server
  // substitutes cannot drift apart.
  const placeholders = useAsync<EmailPlaceholder[]>(
    (signal) => api.get<EmailPlaceholder[]>('/email-templates/placeholders', undefined, signal),
    [],
  )

  const canManage = can('emailtemplate.manage')

  async function act(run: () => Promise<unknown>, success: string) {
    setBusy(true)
    try {
      await run()
      toast.show(success, 'success')
      templates.reload()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'That did not work', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Master data"
        title="Email templates"
        hint="What a quotation is emailed to the customer with. The default is what the Send dialog opens with; the rep can still edit any of it before it goes."
        actions={
          canManage && (
            <Button variant="primary" onClick={() => setEditing('new')}>
              New template
            </Button>
          )
        }
      />

      {templates.error && (
        <ErrorState
          title={templates.error.title}
          detail={templates.error.detail}
          onRetry={templates.reload}
        />
      )}

      <Card>
        <CardHeader
          title={current ? `${current.name} templates` : 'Templates'}
          hint="Scoped to the country in the bar above. Switching country switches the list."
        />

        <Busy busy={templates.loading || busy}>
          {templates.data && templates.data.length === 0 && (
            <EmptyState
              title="No template for this country yet"
              hint={
                canManage
                  ? 'Add one and the Send dialog fills itself in from it. Without one, a rep writes every mail from scratch.'
                  : 'Ask the Director of Sales to add one.'
              }
            />
          )}

          {templates.data && templates.data.length > 0 && (
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Subject</Th>
                  <Th>CC</Th>
                  <Th>Status</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {templates.data.map((template) => (
                  <tr key={template.id}>
                    <Td>
                      <span className="font-medium">{template.name}</span>
                    </Td>
                    <Td className="text-muted">{template.subject}</Td>
                    <Td className="font-mono text-[12px] text-muted">
                      {template.ccRecipients ?? '—'}
                    </Td>
                    <Td>
                      {template.isDefault ? (
                        <Badge tone="accent">Default</Badge>
                      ) : template.isActive ? (
                        <Badge>Active</Badge>
                      ) : (
                        <Badge tone="neutral">Retired</Badge>
                      )}
                    </Td>
                    <Td>
                      <span className="flex justify-end gap-2">
                        <Button size="sm" onClick={() => setEditing(template)}>
                          {canManage ? 'Edit' : 'View'}
                        </Button>

                        {canManage && !template.isDefault && template.isActive && (
                          <Button
                            size="sm"
                            onClick={() =>
                              void act(
                                () => api.put(`/email-templates/${template.id}/default`),
                                'This is now the default',
                              )
                            }
                          >
                            Make default
                          </Button>
                        )}

                        {canManage && (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() =>
                              void act(() => api.del(`/email-templates/${template.id}`), 'Removed')
                            }
                          >
                            Remove
                          </Button>
                        )}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Busy>
      </Card>

      {editing && countryId && (
        <TemplateDialog
          countryId={countryId}
          template={editing === 'new' ? undefined : editing}
          placeholders={placeholders.data ?? []}
          readOnly={!canManage}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined)
            templates.reload()
          }}
        />
      )}
    </>
  )
}

/** What a country's first template starts from, so nobody faces an empty box. */
const STARTING_BODY = [
  '<p>Dear {{CustomerContactName}},</p>',
  '<p>Thank you for your interest in Codemax. Your quotation',
  ' {{QuotationNumber}} is ready for you to read.</p>',
  '<p>Open it here, where you can also download a copy and accept it: ',
  '<a href="{{QuotationLink}}">View and accept your quotation</a></p>',
  '<p>Do let me know if anything needs adjusting.</p>',
  '<p>Kind regards,<br />{{SalespersonName}}</p>',
].join('')

function TemplateDialog({
  countryId,
  template,
  placeholders,
  readOnly,
  onClose,
  onSaved,
}: {
  countryId: string
  template?: EmailTemplate
  placeholders: EmailPlaceholder[]
  readOnly: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const toast = useToast()

  const [name, setName] = useState(template?.name ?? '')
  const [subject, setSubject] = useState(template?.subject ?? 'Quotation {{QuotationNumber}} from Codemax')
  const [cc, setCc] = useState(template?.ccRecipients ?? '')
  const [body, setBody] = useState(template?.bodyHtml ?? STARTING_BODY)
  const [saving, setSaving] = useState(false)

  // Checked while it is typed rather than at Save, because a field that only tells you at
  // Save is a field you retype. The API checks the same thing.
  const ccError = useMemo(() => {
    const parsed = parseAddressList(cc)
    return parsed.invalid ? `${parsed.invalid} is not an email address.` : undefined
  }, [cc])

  const complete = name.trim().length > 0 && subject.trim().length > 0 && !ccError

  async function save() {
    setSaving(true)
    try {
      if (template) {
        await api.put(`/email-templates/${template.id}`, {
          name: name.trim(),
          subject: subject.trim(),
          bodyHtml: body,
          ccRecipients: cc.trim(),
        })
      } else {
        await api.post(`/countries/${countryId}/email-templates`, {
          name: name.trim(),
          subject: subject.trim(),
          bodyHtml: body,
          ccRecipients: cc.trim(),
          isDefault: false,
        })
      }

      toast.show(template ? 'Template saved' : 'Template added', 'success')
      onSaved()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not save it', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      wide
      title={template ? template.name : 'New email template'}
      description="Placeholders are replaced when a mail is composed, so the wording here is what every send starts from."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
          {!readOnly && (
            <Button variant="primary" loading={saving} disabled={!complete} onClick={() => void save()}>
              {template ? 'Save template' : 'Add template'}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" required hint="What a rep picks it by in the Send dialog.">
            {(id) => (
              <Input
                id={id}
                value={name}
                disabled={readOnly}
                onChange={(event) => setName(event.target.value)}
                placeholder="Quotation issue"
              />
            )}
          </Field>

          <Field
            label="CC"
            error={ccError}
            hint="Copied on every mail sent from this template. Editable in the dialog."
          >
            {(id) => (
              <Input
                id={id}
                value={cc}
                disabled={readOnly}
                onChange={(event) => setCc(event.target.value)}
                placeholder="sales.my@codemax.my"
              />
            )}
          </Field>
        </div>

        <Field label="Subject" required>
          {(id) => (
            <Input
              id={id}
              value={subject}
              disabled={readOnly}
              onChange={(event) => setSubject(event.target.value)}
            />
          )}
        </Field>

        <Field label="Message">
          {(id) => (
            <EmailEditor
              id={id}
              value={body}
              disabled={readOnly}
              placeholders={placeholders}
              onChange={setBody}
              placeholder="What the customer will read"
            />
          )}
        </Field>

        {placeholders.length > 0 && (
          <p className="text-[12.5px] text-muted">
            Placeholders:{' '}
            {placeholders.map((item, index) => (
              <span key={item.token}>
                {index > 0 && ', '}
                <code className="rounded bg-sunken px-1 py-0.5 font-mono text-[11.5px]">
                  {`{{${item.token}}}`}
                </code>{' '}
                {item.label.toLowerCase()}
              </span>
            ))}
            . The signing link is minted as the mail is sent, so it is the one that stays a
            placeholder in the dialog.
          </p>
        )}
      </div>
    </Dialog>
  )
}
